import React from 'react';
import { Container, Typography, Box, useTheme, Link as MuiLink, Paper } from '@mui/material';
import { Link } from 'react-router-dom';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { LegalSectionBody } from './LegalSectionBody';

const contentUseCase = new ContentUseCase();

export const PrivacyPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const { privacy } = contentUseCase.getLegalContent(language);

  return (
    <Container maxWidth="md" sx={{ py: theme.spacing(8) }}>
      <SEO page="privacy" />
      <Typography variant="h2" component="h1" gutterBottom align="center" color="primary" sx={{ mb: theme.spacing(6) }}>
        {privacy.title}
      </Typography>
        {/* Website part */}
        <Box sx={{ mb: theme.spacing(4) }}>
          <Typography variant="h3" gutterBottom color="primary">
            {privacy.websiteIntro.title}
          </Typography>
          <Typography variant="body1" color="text.primary">
            {privacy.websiteIntro.description}
          </Typography>
        </Box>

        {/* Responsible */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h4" gutterBottom color="primary">
            {privacy.responsible.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.responsible.description}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.responsible.initiative}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }} color="text.primary">{privacy.responsible.name}</Typography>
          <Typography variant="body1" color="text.primary">{privacy.responsible.address}</Typography>
          <Typography variant="body1" color="text.primary">{privacy.responsible.city}</Typography>
          <Typography variant="body1" color="text.primary">{privacy.responsible.country}</Typography>
          <Typography variant="body1" sx={{ mt: 1 }} color="text.primary">{privacy.responsible.email}</Typography>
        </Box>

        {/* General Information */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h4" gutterBottom color="primary">
            {privacy.general.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.general.description}
          </Typography>
          <Typography variant="body1" paragraph sx={{ fontWeight: 600 }} color="text.primary">
            {privacy.general.principle}
          </Typography>
        </Box>

        {/* Automatically Collected Data */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h4" gutterBottom color="primary">
            {privacy.automatic.title}
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }} color="text.primary">
            {privacy.automatic.server.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.automatic.server.description}
          </Typography>
          <ul>
            {privacy.automatic.server.data.map((item, idx) => (
              <li key={idx}>
                <Typography variant="body1" color="text.primary">{item}</Typography>
              </li>
            ))}
          </ul>
          <Typography variant="body2" paragraph sx={{ mt: 2, fontStyle: 'italic' }} color="text.secondary">
            {privacy.automatic.legal}
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }} color="text.secondary">
            {privacy.automatic.retention}
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }} color="text.secondary">
            {privacy.automatic.transfer}
          </Typography>
        </Box>

        {/* Waitlist form */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h4" gutterBottom color="primary">
            {privacy.waitlist.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.waitlist.description}
          </Typography>
          <ul>
            {privacy.waitlist.data.map((item, idx) => (
              <li key={idx}>
                <Typography variant="body1" color="text.primary">{item}</Typography>
              </li>
            ))}
          </ul>

          {[privacy.waitlist.purpose, privacy.waitlist.sensitive, privacy.waitlist.processors].map((block, idx) => (
            <React.Fragment key={idx}>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }} color="text.primary">
                {block.title}
              </Typography>
              <Typography variant="body1" paragraph color="text.primary">
                {block.description}
              </Typography>
            </React.Fragment>
          ))}

          <Typography variant="body2" paragraph sx={{ mt: 2, fontStyle: 'italic' }} color="text.secondary">
            {privacy.waitlist.legal}
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }} color="text.secondary">
            {privacy.waitlist.retention}
          </Typography>
          <Typography variant="body2" paragraph sx={{ fontStyle: 'italic' }} color="text.secondary">
            {privacy.waitlist.withdrawal}
          </Typography>
        </Box>

        {/* Cookies */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h4" gutterBottom color="primary">
            {privacy.cookies.title}
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }} color="text.primary">
            {privacy.cookies.necessary.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.cookies.necessary.description}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }} color="text.primary">
            {privacy.cookies.analytics.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.cookies.analytics.description}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }} color="text.primary">
            {privacy.cookies.tracking.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.cookies.tracking.description}
          </Typography>

          <Typography variant="body2" paragraph sx={{ mt: 2, fontStyle: 'italic' }} color="text.secondary">
            {privacy.cookies.legal}
          </Typography>
        </Box>

        {/* External Links */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h4" gutterBottom color="primary">
            {privacy.external.title}
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 3 }} color="text.primary">
            {privacy.external.donations.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.external.donations.description}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }} color="text.primary">
            {privacy.external.social.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.external.social.description}
          </Typography>
        </Box>

        {/* Rights */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h4" gutterBottom color="primary">
            {privacy.rights.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.rights.description}
          </Typography>
          <ul>
            {privacy.rights.list.map((right, idx) => (
              <li key={idx}>
                <Typography variant="body1" color="text.primary">{right}</Typography>
              </li>
            ))}
          </ul>
          <Typography variant="body2" paragraph sx={{ mt: 2, fontStyle: 'italic' }} color="text.secondary">
            {privacy.rights.contact}
          </Typography>
        </Box>

        {/* Changes */}
        <Box sx={{ mb: theme.spacing(6) }}>
          <Typography variant="h4" gutterBottom color="primary">
            {privacy.changes.title}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.changes.description}
          </Typography>
        </Box>

        {/* App privacy policy — verbatim mirror of the in-app declaration (v1.7) */}
        <Box sx={{ mb: theme.spacing(4) }}>
          <Typography variant="h3" gutterBottom color="primary" sx={{ mt: theme.spacing(4) }}>
            {privacy.appIntro.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: theme.spacing(2) }}>
            {privacy.appIntro.version}
          </Typography>
          <Typography variant="body1" paragraph color="text.primary">
            {privacy.appIntro.description}
          </Typography>
          <Paper
            variant="outlined"
            sx={{ p: theme.spacing(3), mt: theme.spacing(3), backgroundColor: 'transparent' }}
          >
            <Typography variant="body1" sx={{ fontWeight: 600 }} color="text.primary" gutterBottom>
              {privacy.appIntro.summaryLabel}
            </Typography>
            <Typography variant="body1" color="text.primary">
              {privacy.appIntro.summary}
            </Typography>
          </Paper>
        </Box>

        {privacy.appSections.map((section, idx) => (
          <Box key={idx} sx={{ mb: theme.spacing(6) }}>
            <Typography variant="h4" gutterBottom color="primary">
              {section.heading}
            </Typography>
            <LegalSectionBody body={section.body} />
            {'internalLink' in section && section.internalLink && (
              <MuiLink
                component={Link}
                to={section.internalLink.to}
                color="primary"
                underline="hover"
                sx={{ fontWeight: 600 }}
              >
                {section.internalLink.text}
              </MuiLink>
            )}
            {'externalLink' in section && section.externalLink && (
              <MuiLink
                href={section.externalLink.href}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
                underline="hover"
              >
                {section.externalLink.text}
              </MuiLink>
            )}
          </Box>
        ))}
    </Container>
  );
};

